import { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { IoClose } from 'react-icons/io5';
import { ColorTheme, TextTheme } from '../../../components/globaStyledComponents/globalStyledComponentsStyling';
import { CrossButton } from '../../../components/globalPopup/GlobalPopupContainerStyle';
import { isHotel } from '../../../utils/listingUtils';

interface ViewListingLightboxProps {
    images: string[];
    initialIndex: number;
    onClose: () => void;
    listingTitle: string;
    listingId: string;
    hotelRooms?: any[];
    propertyDetails: {
        guests: number;
        bedrooms: number;
        beds: number;
        baths: number;
        area: number;
    };
}

const LightboxContainer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background-color: ${ColorTheme.White.color};
    z-index: 1000;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
`;

const MainCloseButton = styled(CrossButton)`
    position: fixed;
    top: 32px;
    left: 32px;
    z-index: 1002;
    
    @media (max-width: 767px) {
        top: 16px;
        left: 16px;
    }
`;

const FullscreenCloseButton = styled.button`
    position: fixed;
    top: 32px;
    left: 32px;
    background: none;
    border: 1px solid ${ColorTheme.White.color};
    cursor: pointer;
    padding: 6px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    z-index: 1004;
    
    @media (max-width: 767px) {
        top: 16px;
        left: 16px;
    }
    
    &:hover {
        background-color: rgba(255, 255, 255, 0.2);
    }
`;



const ListingTitle = styled.h1`
    font-size: ${TextTheme.sans_700_semi.fontSize};
    font-family: ${TextTheme.sans_700_semi.fontFamily};
    line-height: ${TextTheme.sans_700_semi.lineHeight};
    text-align: center;
    margin: 64px 24px 0;
    padding: 0;
    
    @media (min-width: 768px) {
        font-size: ${TextTheme.sans_800_semi.fontSize};
        line-height: ${TextTheme.sans_800_semi.lineHeight};
        margin: 48px auto 0;
        max-width: 1024px;
    }
`;

const PropertyDetails = styled.p`
    font-size: ${TextTheme.sans_300.fontSize};
    font-family: ${TextTheme.sans_300.fontFamily};
    line-height: ${TextTheme.sans_300.lineHeight};
    color: ${ColorTheme.Grey.color};
    text-align: center;
    margin: 8px 24px 0;
    padding: 0;
    
    @media (min-width: 768px) {
        margin: 8px auto 0;
        max-width: 1024px;
    }
`;

const GalleryContainer = styled.div`
    padding: 40px 0 80px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 8px;
    width: 100%;
    box-sizing: border-box;
    
    @media (min-width: 768px) {
        padding: 40px 8px 80px;
        max-width: 1024px;
        margin-left: auto;
        margin-right: auto;
        grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
        gap: 12px;
    }
`;

const ImageWrapper = styled.div<{ isLarge?: boolean }>`
    position: relative;
    cursor: pointer;
    overflow: hidden;
    border-radius: 8px;
    
    ${props => props.isLarge && `
        grid-column: span 2;
        grid-row: span 2;
    `}
    
    &:hover {
        opacity: 0.9;
    }
    
    @media (max-width: 767px) {
        ${props => props.isLarge && `
            grid-column: span 1;
            grid-row: span 1;
        `}
    }
`;

const GalleryImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    vertical-align: middle;
`;

// Fullscreen modal for individual image view
const FullscreenModal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #000000;
    z-index: 1002;
    display: flex;
    align-items: center;
    justify-content: center;
    -webkit-overflow-scrolling: touch;
`;

const FullscreenImage = styled.img`
    max-width: 90vw;
    max-height: calc(100vh - 160px);
    object-fit: contain;
`;

const ImageCount = styled.p`
    position: fixed;
    bottom: 32px;
    left: 50%;
    transform: translateX(-50%);
    color: white;
    font-size: ${TextTheme.sans_400.fontSize};
    font-family: ${TextTheme.sans_400.fontFamily};
    line-height: ${TextTheme.sans_400.lineHeight};
    z-index: 1003;
`;

const NavigationButton = styled.button<{ direction: 'left' | 'right' }>`
    position: fixed;
    ${props => props.direction}: 20px;
    top: 50%;
    transform: translateY(-50%) ${props => props.direction === 'right' ? 'rotate(180deg)' : 'none'};
    background: transparent;
    border: 1px solid white;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 1003;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
`;

const NavigationIcon = styled.img`
    width: 12px;
    height: 12px;
    filter: brightness(0) invert(1);
`;

const ViewListingLightbox = ({ images, initialIndex, onClose, listingTitle, listingId, hotelRooms, propertyDetails }: ViewListingLightboxProps) => {
    const [selectedImage, setSelectedImage] = useState(initialIndex);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Calculate max capacity from hotel rooms for hotels
    const getMaxCapacity = () => {
        if (isHotel(listingId) && hotelRooms && hotelRooms.length > 0) {
            return Math.max(...hotelRooms.map(room => room.capacity || 0));
        }
        return propertyDetails.guests;
    };

    // Get deduplicated room count for hotels
    const getDeduplicatedRoomCount = () => {
        if (isHotel(listingId) && hotelRooms && hotelRooms.length > 0) {
            const uniqueRooms = hotelRooms.filter((room, index, self) => 
                index === self.findIndex(r => r.name === room.name)
            );
            return uniqueRooms.length;
        }
        return 0;
    };

    useEffect(() => {
        // Hide both body scrolling and the navbar
        document.body.style.overflow = 'hidden';
        
        // Hide both the navbar and its container
        const navbarContainer = document.querySelector('.listing-navbar-container') as HTMLElement;
        const navbar = document.querySelector('.listing-navbar') as HTMLElement;
        
        if (navbarContainer) {
            navbarContainer.style.display = 'none';
        }
        if (navbar) {
            navbar.style.display = 'none';
        }
        
        return () => {
            document.body.style.overflow = 'unset';
            if (navbarContainer) {
                navbarContainer.style.display = '';
            }
            if (navbar) {
                navbar.style.display = '';
            }
        };
    }, []);

    const handleImageClick = (index: number) => {
        setSelectedImage(index);
        setIsFullscreen(true);
    };

    const handlePrevImage = useCallback(() => {
        setSelectedImage((prev) => (prev - 1 + images.length) % images.length);
    }, [images.length]);

    const handleNextImage = useCallback(() => {
        setSelectedImage((prev) => (prev + 1) % images.length);
    }, [images.length]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (isFullscreen) {
                if (e.key === 'ArrowLeft') handlePrevImage();
                if (e.key === 'ArrowRight') handleNextImage();
                if (e.key === 'Escape') setIsFullscreen(false);
            } else if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isFullscreen, handlePrevImage, handleNextImage, onClose]);

    const renderImages = () => {
        return images.map((image, index) => {
            // Create a pattern where every 3rd image is large, or first image is always large
            const isLarge = index === 0 || index % 3 === 0;
            
            return (
                <ImageWrapper 
                    key={index} 
                    onClick={() => handleImageClick(index)} 
                    isLarge={isLarge}
                >
                    <GalleryImage src={image} alt={`Gallery image ${index + 1}`} loading="lazy" />
                </ImageWrapper>
            );
        });
    };

    return (
        <LightboxContainer>
            <MainCloseButton onClick={onClose}>
                <img 
                    src="https://d292awxalydr86.cloudfront.net/Universal+icons/Cross.svg"
                    alt="Close"
                    width={16}
                    height={16}
                />
            </MainCloseButton>
            <ListingTitle>{listingTitle}</ListingTitle>
            <PropertyDetails>
                {(() => {
                    if (isHotel(listingId)) {
                        const maxCapacity = getMaxCapacity();
                        const roomCount = getDeduplicatedRoomCount();
                        return (
                            <>
                                {maxCapacity} {maxCapacity === 1 ? 'guest' : 'guests'} · {roomCount} {roomCount === 1 ? 'room type' : 'room types'}
                                {propertyDetails.area > 0 && ` · ${propertyDetails.area} m²`}
                            </>
                        );
                    } else {
                        return (
                            <>
                                {propertyDetails.guests} {propertyDetails.guests === 1 ? 'guest' : 'guests'} · {propertyDetails.bedrooms} {propertyDetails.bedrooms === 1 ? 'bedroom' : 'bedrooms'} · {propertyDetails.beds} {propertyDetails.beds === 1 ? 'bed' : 'beds'} · {propertyDetails.baths} {propertyDetails.baths === 1 ? 'bath' : 'baths'}
                                {propertyDetails.area > 0 && ` · ${propertyDetails.area} m²`}
                            </>
                        );
                    }
                })()}
            </PropertyDetails>
            <GalleryContainer ref={containerRef}>
                {renderImages()}
            </GalleryContainer>

            {isFullscreen && (
                <FullscreenModal onClick={() => setIsFullscreen(false)}>
                    <FullscreenCloseButton onClick={() => setIsFullscreen(false)}>
                        <IoClose style={{ height: '20px', width: '20px', fill: 'white' }} />
                    </FullscreenCloseButton>
                    <NavigationButton 
                        direction="left" 
                        onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}
                    >
                        <NavigationIcon src="https://d292awxalydr86.cloudfront.net/Universal+icons/Arrow.svg" alt="Previous" />
                    </NavigationButton>
                    <FullscreenImage 
                        src={images[selectedImage]} 
                        alt={`Full screen view ${selectedImage + 1}`}
                        onClick={(e) => e.stopPropagation()}
                    />
                    <ImageCount>{selectedImage + 1} of {images.length}</ImageCount>
                    <NavigationButton 
                        direction="right" 
                        onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
                    >
                        <NavigationIcon src="https://d292awxalydr86.cloudfront.net/Universal+icons/Arrow.svg" alt="Next" />
                    </NavigationButton>
                </FullscreenModal>
            )}
        </LightboxContainer>
    );
};

export default ViewListingLightbox; 